import { connectRedux, setFormSessionValues, GoToComponentProps, getFormSessionValue } from '@common/utils'
import {
    BuyCheckoutFormContainerActionsType,
    mapBuyCheckoutFormContainerActions,
    mapBuyCheckoutFormContainerState,
} from '@buy/connectedComponents/CheckoutFormContainer/connector'
import { BuyCheckoutFormContainerState } from '@buy/state/checkout-form-container/state'
import { BuyFormData } from '@buy/state/buy-form/state'
import { CheckoutSchema } from '@buy/utils'
import { Formik, FormikProps } from 'formik'
import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CHECKOUTFORM_FIELDS } from '@buy/constants'
import { getCountryFromCookie } from '@common/utils/cookie-utils'
import { isEmpty } from 'lodash-es'
import { COUNTRIES } from '@common/constants'
import { getIsOnePayFlow } from '@buy/utils/get-is-onepay-flow'
import { useSearchParams } from 'react-router-dom'
import { showDisclaimer } from '@common/constants/search-query-params'
import { ConnectedCheckoutForm } from '../CheckoutForm/index'
import { ConnectedCheckoutFormOnePay } from '../CheckoutForm/index-onepay'

type BuyCheckoutFormContainerProps = GoToComponentProps<
    BuyCheckoutFormContainerState,
    BuyCheckoutFormContainerActionsType,
    {
        isTabletOrMobileView?: boolean
        isMobileView?: boolean
        setHasAddressValidationErrors: React.Dispatch<React.SetStateAction<boolean>>
        setGeneralFieldErrors: React.Dispatch<React.SetStateAction<boolean>>
        setHasCardValidationErrors: React.Dispatch<React.SetStateAction<boolean>>
        hasAddressValidationErrors: boolean
        generalFieldErrors: boolean
        hasCardValidationErrors: boolean
    }
>

export interface CountryCodeInfo {
    current: string
    previous?: string
}

export const BuyCheckoutFormContainer: FunctionComponent<BuyCheckoutFormContainerProps> = ({
    state,
    props,
    actions,
}) => {
    const formikRef = useRef<FormikProps<BuyFormData>>(null)
    const isSubmittingRef = useRef<boolean>(false)
    const isOnePayFlow = getIsOnePayFlow()
    const [countryCodeInfo, setCountryCodeInfo] = useState<CountryCodeInfo>({ current: '' })
    const [searchParams] = useSearchParams()

    useLayoutEffect(() => {
        return () => {
            const formRef = { ...formikRef }
            if (!isEmpty(formRef.current)) {
                // eslint-disable-next-line prefer-const
                let formValues = { ...formRef.current!.values }
                if (!isEmpty(formValues)) formValues!.ExpirationDate = ''
                if (formValues) actions.updateInitialFormState(formValues)
            }
        }
    }, [actions])

    const setFormValues = async (fieldValues: Record<string, string | boolean>) => {
        await formikRef.current?.setValues((values) => ({ ...values, ...fieldValues }))
    }

    useEffect(() => {
        const {
            BillingFirstName,
            BillingLastName,
            City,
            ContactPhone,
            FirstName,
            LastName,
            StateCode,
            Street1,
            ZipCode,
            CountryCode,
            Email,
            Disclaimer,
        } = state.initialData
        const isSameAsAccount = sessionStorage.getItem('useSameForBilling') !== 'false'
        const defaultCountryCode =
            getFormSessionValue(CHECKOUTFORM_FIELDS.COUNTRY_CODE) || getCountryFromCookie() || COUNTRIES.US
        const selectedCountry = isEmpty(CountryCode) ? defaultCountryCode : CountryCode
        setCountryCodeInfo({ current: selectedCountry, previous: '' })
        const formValues = {
            [CHECKOUTFORM_FIELDS.FIRST_NAME]: FirstName,
            [CHECKOUTFORM_FIELDS.LAST_NAME]: LastName,
            [CHECKOUTFORM_FIELDS.COUNTRY_CODE]: selectedCountry,
            [CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME]: isSameAsAccount ? FirstName : BillingFirstName,
            [CHECKOUTFORM_FIELDS.BILLING_LAST_NAME]: isSameAsAccount ? LastName : BillingLastName,
            [CHECKOUTFORM_FIELDS.STREET1]: Street1,
            [CHECKOUTFORM_FIELDS.CITY]: City,
            [CHECKOUTFORM_FIELDS.ZIP_CODE]: ZipCode,
            [CHECKOUTFORM_FIELDS.CONTACT_PHONE]: ContactPhone,
            [CHECKOUTFORM_FIELDS.STATE_CODE]: StateCode,
            [CHECKOUTFORM_FIELDS.EMAIL]: Email,
            [CHECKOUTFORM_FIELDS.IS_DISCLAIMER_AGREED]: Disclaimer,
        }
        setFormSessionValues(formValues)
        setFormValues(formValues)
    }, [state.initialData])

    useEffect(() => {
        if (state.location.isCountriesLoaded && !isEmpty(countryCodeInfo.current)) {
            const preSelectedCountry = state.location.countries.find(
                (eligibleCountry) => countryCodeInfo.current === eligibleCountry.abbreviation
            )
            setCountryCodeInfo({ current: preSelectedCountry?.abbreviation || COUNTRIES.US })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.location.isCountriesLoaded])

    return (
        <>
            <Formik
                initialValues={state.initialData}
                validationSchema={() =>
                    CheckoutSchema(
                        countryCodeInfo.current,
                        state.errorContent,
                        searchParams.get(showDisclaimer) === 'true'
                    )
                }
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={(_values, formikBag) => {
                    formikBag.setSubmitting(false)
                }}
                innerRef={formikRef}
            >
                {(formikProps) => {
                    if (formikProps.isSubmitting) {
                        isSubmittingRef.current = true
                    }

                    if (isSubmittingRef.current && !formikProps.isSubmitting) {
                        isSubmittingRef.current = false
                    }

                    if (isOnePayFlow) {
                        return (
                            <ConnectedCheckoutFormOnePay
                                {...props}
                                initialData={state.initialData}
                                errorContent={state.errorContent}
                                formikRef={formikRef}
                                isSubmittingRef={isSubmittingRef}
                                countryCodeInfo={countryCodeInfo}
                                setCountryCodeInfo={setCountryCodeInfo}
                                formikProps={formikProps}
                            />
                        )
                    }
                    return (
                        <ConnectedCheckoutForm
                            {...props}
                            initialData={state.initialData}
                            errorContent={state.errorContent}
                            formikRef={formikRef}
                            isSubmittingRef={isSubmittingRef}
                            countryCodeInfo={countryCodeInfo}
                            setCountryCodeInfo={setCountryCodeInfo}
                        />
                    )
                }}
            </Formik>
        </>
    )
}

export const ConnectedBuyCheckoutFormContainer = connectRedux(
    BuyCheckoutFormContainer,
    mapBuyCheckoutFormContainerState,
    mapBuyCheckoutFormContainerActions
)
